import AmbientGlow from '../components/AmbientGlow';
/**
 * StoreManagerPage - Artist store manager
 * Tabs: Products (add/edit/delete) and Orders (view + fulfill).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Plus, Edit2, Trash2, Package, ShoppingBag, Loader2, Truck, Check, X, ImagePlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'One Size'];

const emptyForm = { name: '', description: '', price: '', category: 'merch', stock: '10', image: '', sizes: [] };

// Auto-resize any image to a uniform square (center-crop "cover"), returns a JPEG Blob.
const resizeImageToSquare = (file, size = 1200) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    URL.revokeObjectURL(url);
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0B0A10';
    ctx.fillRect(0, 0, size, size);
    const scale = Math.max(size / img.width, size / img.height);
    const w = img.width * scale, h = img.height * scale;
    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('resize failed')), 'image/jpeg', 0.9);
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('load failed')); };
  img.src = url;
});

const ProductForm = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(initial
    ? { name: initial.name || '', description: initial.description || '', price: String(initial.price ?? ''), category: initial.category || 'merch', stock: String(initial.stock ?? '0'), image: initial.images?.[0] || '', sizes: initial.sizes || [] }
    : emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSize = (s) => setForm((f) => ({
    ...f,
    sizes: f.sizes.includes(s) ? f.sizes.filter((x) => x !== s) : [...f.sizes, s],
  }));

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please choose an image file.'); return; }
    setUploading(true); setError('');
    try {
      const token = localStorage.getItem('token');
      // Auto-resize to a uniform 1200x1200 square so all storefront thumbnails match.
      const blob = await resizeImageToSquare(file, 1200);
      const ct = 'image/jpeg';
      const presign = await axios.get(`${API_URL}/api/radio/media/presigned-upload`, {
        params: { filename: 'merch.jpg', media_type: 'image', content_type: ct },
        headers: { Authorization: `Bearer ${token}` },
      });
      const putRes = await fetch(presign.data.upload_url, { method: 'PUT', headers: { 'Content-Type': ct }, body: blob });
      if (!putRes.ok) throw new Error('upload failed');
      up('image', presign.data.public_url);
    } catch (err) {
      setError('Image upload failed. Try again or paste a URL.');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setError('');
    if (!form.name.trim() || !form.price || Number(form.price) <= 0) { setError('Enter a name and valid price.'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        price: Number(form.price),
        category: form.category,
        stock: Number(form.stock) || 0,
        images: form.image.trim() ? [form.image.trim()] : [],
        sizes: form.category === 'merch' ? form.sizes : []
      };
      if (isEdit) {
        await axios.put(`${API_URL}/api/store/products/${initial.id}`, payload, { headers });
      } else {
        await axios.post(`${API_URL}/api/store/products`, payload, { headers });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save product.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-md z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
          className="w-full max-w-md bg-[#12101A] rounded-3xl border border-white/10 max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()} data-testid="product-form-modal">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-white font-bold text-lg">{isEdit ? 'Edit Product' : 'Add Product'}</h3>
            <button onClick={onClose} className="text-[#71717A] hover:text-white" data-testid="product-form-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-5 space-y-4">
            {error && <div className="p-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-300 text-sm">{error}</div>}
            <input value={form.name} onChange={(e) => up('name', e.target.value)} placeholder="Product name"
              className="w-full bg-white/5 rounded-xl px-4 py-3 text-white outline-none placeholder-[#52525B]" data-testid="pf-name" />
            <textarea value={form.description} onChange={(e) => up('description', e.target.value)} placeholder="Description" rows={2}
              className="w-full bg-white/5 rounded-xl px-4 py-3 text-white outline-none placeholder-[#52525B]" data-testid="pf-desc" />
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 bg-white/5 rounded-xl px-3">
                <span className="text-[#A1A1AA]">$</span>
                <input type="number" min="0.01" value={form.price} onChange={(e) => up('price', e.target.value)} placeholder="Price"
                  className="flex-1 bg-transparent py-3 text-white outline-none" data-testid="pf-price" />
              </div>
              <input type="number" min="0" value={form.stock} onChange={(e) => up('stock', e.target.value)} placeholder="Stock"
                className="bg-white/5 rounded-xl px-4 py-3 text-white outline-none placeholder-[#52525B]" data-testid="pf-stock" />
            </div>
            <select value={form.category} onChange={(e) => up('category', e.target.value)}
              className="w-full bg-white/5 rounded-xl px-4 py-3 text-white outline-none" data-testid="pf-category">
              <option value="merch">Merch</option>
              <option value="music">Music</option>
              <option value="tickets">Tickets</option>
            </select>
            {/* Apparel sizes (merch only) */}
            {form.category === 'merch' && (
              <div data-testid="pf-sizes">
                <label className="text-[#A1A1AA] text-xs mb-1.5 block">Available sizes (apparel only — leave empty for non-apparel)</label>
                <div className="flex flex-wrap gap-2">
                  {SIZE_OPTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleSize(s)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${form.sizes.includes(s) ? 'bg-[#00F0FF] text-[#050505]' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'}`}
                      data-testid={`pf-size-${s.replace(/\s+/g, '-')}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {form.sizes.length > 0 && (
                  <p className="text-[#71717A] text-[11px] mt-1.5">Shoppers must pick a size before buying.</p>
                )}
              </div>
            )}
            {/* Product photo upload */}
            <div>
              <label className="text-[#A1A1AA] text-xs mb-1.5 block">Product photo</label>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleImage} className="hidden" data-testid="pf-image-file" />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="relative w-24 h-24 rounded-xl border border-dashed border-white/20 bg-white/5 hover:bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0"
                  data-testid="pf-upload-photo"
                >
                  {uploading ? (
                    <Loader2 className="w-5 h-5 text-[#00F0FF] animate-spin" />
                  ) : form.image ? (
                    <img src={form.image} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center text-[#71717A]"><ImagePlus className="w-6 h-6 mx-auto" /><span className="text-[10px]">Upload</span></div>
                  )}
                </button>
                <div className="flex-1">
                  <p className="text-[#71717A] text-xs">Upload a photo — we'll auto-crop it to a clean square. Or paste an image URL.</p>
                  <input value={form.image} onChange={(e) => up('image', e.target.value)} placeholder="Image URL (optional)"
                    className="w-full mt-2 bg-white/5 rounded-xl px-3 py-2 text-white text-sm outline-none placeholder-[#52525B]" data-testid="pf-image" />
                </div>
              </div>
            </div>
            <button onClick={submit} disabled={saving || uploading}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold disabled:opacity-50" data-testid="pf-save">
              {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Product')}
            </button>
          </div>
        </motion.div>
      </div>
    </>
  );
};

const OrderCard = ({ order, onFulfilled }) => {
  const [tracking, setTracking] = useState(order.tracking_number || '');
  const [busy, setBusy] = useState(false);
  const shipped = ['shipped', 'delivered'].includes(order.status);

  const fulfill = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/store/artist/orders/${order.id}/fulfill`,
        { status: 'shipped', tracking_number: tracking },
        { headers: { Authorization: `Bearer ${token}` } });
      onFulfilled();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#12101A] rounded-2xl border border-white/5 p-4" data-testid={`order-card-${order.id}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[#71717A] text-xs">#{order.id.slice(0, 8)}</span>
        <span className={`text-xs px-2 py-1 rounded-full ${shipped ? 'bg-green-500/15 text-green-400' : 'bg-amber-500/15 text-amber-400'}`}>
          {order.status}
        </span>
      </div>
      {order.items.map((it, i) => (
        <div key={i} className="flex items-center gap-3 py-1">
          <div className="w-10 h-10 rounded-lg bg-white/10 overflow-hidden flex-shrink-0">
            {it.image_url ? <img src={it.image_url} alt={it.product_name} className="w-full h-full object-cover" /> : <Package className="w-5 h-5 m-2.5 text-[#71717A]" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm truncate">{it.product_name}</p>
            <p className="text-[#71717A] text-xs">Qty {it.quantity} · ${Number(it.price).toFixed(2)}{it.size ? ` · ${it.size}` : ''}{it.color ? ` · ${it.color}` : ''}</p>
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
        <span className="text-[#A1A1AA] text-sm">Your total</span>
        <span className="text-white font-semibold">${Number(order.artist_total || 0).toFixed(2)}</span>
      </div>
      {order.shipping_address && (
        <p className="text-[#71717A] text-xs mt-2">Ship to: {order.shipping_address.full_name}, {order.shipping_address.city} {order.shipping_address.state}</p>
      )}
      {!shipped ? (
        <div className="flex gap-2 mt-3">
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking # (optional)"
            className="flex-1 bg-white/5 rounded-lg px-3 py-2 text-white text-sm outline-none placeholder-[#52525B]" data-testid={`order-tracking-${order.id}`} />
          <button onClick={fulfill} disabled={busy}
            className="px-3 py-2 rounded-lg bg-[#00F0FF] text-[#050505] text-sm font-semibold flex items-center gap-1 disabled:opacity-50" data-testid={`order-fulfill-${order.id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Truck className="w-4 h-4" /> Ship</>}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1 mt-3 text-green-400 text-sm">
          <Check className="w-4 h-4" /> Fulfilled{order.tracking_number ? ` · ${order.tracking_number}` : ''}
        </div>
      )}
    </div>
  );
};

const RELEASE_TYPES = [['single', 'Single'], ['album', 'Album'], ['cover', 'Cover']];

const ReleaseForm = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(initial
    ? { title: initial.title || '', description: initial.description || '', release_type: initial.release_type || 'single', release_date: initial.release_date || '', cover_image: initial.cover_image || '', preorder_enabled: !!initial.preorder_enabled, preorder_price: initial.preorder_price != null ? String(initial.preorder_price) : '' }
    : { title: '', description: '', release_type: 'single', release_date: '', cover_image: '', preorder_enabled: false, preorder_price: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true); setError('');
    try {
      const token = localStorage.getItem('token');
      const blob = await resizeImageToSquare(file, 1200);
      const presign = await axios.get(`${API_URL}/api/radio/media/presigned-upload`, {
        params: { filename: 'cover.jpg', media_type: 'image', content_type: 'image/jpeg' },
        headers: { Authorization: `Bearer ${token}` },
      });
      const putRes = await fetch(presign.data.upload_url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blob });
      if (!putRes.ok) throw new Error('upload failed');
      up('cover_image', presign.data.public_url);
    } catch { setError('Cover upload failed. Try again.'); } finally { setUploading(false); }
  };

  const submit = async () => {
    setError('');
    if (!form.title.trim()) { setError('Enter a title.'); return; }
    if (!form.release_date) { setError('Pick a release date.'); return; }
    if (form.preorder_enabled && (!form.preorder_price || Number(form.preorder_price) <= 0)) { setError('Enter a valid pre-order price.'); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        release_type: form.release_type,
        release_date: form.release_date,
        cover_image: form.cover_image,
        preorder_enabled: form.preorder_enabled,
        preorder_price: form.preorder_enabled ? Number(form.preorder_price) : null,
      };
      if (isEdit) await axios.put(`${API_URL}/api/artists/releases/${initial.id}`, payload, { headers });
      else await axios.post(`${API_URL}/api/artists/releases`, payload, { headers });
      onSaved(); onClose();
    } catch (e) { setError(e?.response?.data?.detail || 'Could not save release.'); } finally { setSaving(false); }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 backdrop-blur-md z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          className="w-full sm:max-w-md bg-[#12101A] rounded-t-3xl sm:rounded-2xl border border-white/10 p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="release-form-modal">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>{isEdit ? 'Edit Release' : 'New Release'}</h2>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10 text-white/60"><X className="w-5 h-5" /></button>
          </div>

          <button onClick={() => fileRef.current?.click()} className="w-full aspect-square max-h-52 mx-auto rounded-2xl bg-white/5 border border-dashed border-white/20 flex items-center justify-center overflow-hidden mb-4" data-testid="rf-cover">
            {form.cover_image ? <img src={form.cover_image} alt="cover" className="w-full h-full object-cover" /> : (
              <div className="text-center text-[#71717A]">{uploading ? <Loader2 className="w-7 h-7 animate-spin mx-auto" /> : <><ImagePlus className="w-7 h-7 mx-auto mb-1 text-[#00F0FF]" /><p className="text-xs">Add cover art</p></>}</div>
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImage} />

          <input value={form.title} onChange={(e) => up('title', e.target.value)} placeholder="Release title" className="w-full bg-white/5 rounded-xl px-4 py-3 text-white outline-none placeholder-[#52525B] mb-3" data-testid="rf-title" />

          <div className="flex gap-2 mb-3">
            {RELEASE_TYPES.map(([val, label]) => (
              <button key={val} onClick={() => up('release_type', val)}
                className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-colors ${form.release_type === val ? 'bg-[#00F0FF] text-[#050505]' : 'bg-white/5 text-[#A1A1AA]'}`}
                data-testid={`rf-type-${val}`}>{label}</button>
            ))}
          </div>

          <label className="text-[#71717A] text-xs mb-1 block">Release date</label>
          <input type="date" min={today} value={form.release_date} onChange={(e) => up('release_date', e.target.value)} className="w-full bg-white/5 rounded-xl px-4 py-3 text-white outline-none mb-3" data-testid="rf-date" />

          <textarea value={form.description} onChange={(e) => up('description', e.target.value)} placeholder="Description (optional)" rows={2} className="w-full bg-white/5 rounded-xl px-4 py-3 text-white outline-none placeholder-[#52525B] resize-none mb-3" />

          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 mb-3">
            <div>
              <p className="text-white text-sm font-medium">Enable pre-order</p>
              <p className="text-[#71717A] text-xs">Fans pay now via Stripe</p>
            </div>
            <button onClick={() => up('preorder_enabled', !form.preorder_enabled)}
              className={`w-12 h-6 rounded-full transition-colors ${form.preorder_enabled ? 'bg-[#00F0FF]' : 'bg-white/20'}`} data-testid="rf-preorder-toggle">
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${form.preorder_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
          {form.preorder_enabled && (
            <div className="relative mb-3">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717A]">$</span>
              <input type="number" min="0" step="0.01" value={form.preorder_price} onChange={(e) => up('preorder_price', e.target.value)} placeholder="Pre-order price" className="w-full bg-white/5 rounded-xl pl-8 pr-4 py-3 text-white outline-none placeholder-[#52525B]" data-testid="rf-price" />
            </div>
          )}

          {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
          <button onClick={submit} disabled={saving || uploading} className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold disabled:opacity-50" data-testid="rf-save">
            {saving ? 'Saving…' : isEdit ? 'Save Release' : 'Create Release'}
          </button>
        </motion.div>
      </div>
    </>
  );
};


const StoreManagerPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [releases, setReleases] = useState([]);
  const [releaseFormOpen, setReleaseFormOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);

  const load = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // Ensure the seller has a profile (auto-provisioned) so creating products won't 403.
      await axios.get(`${API_URL}/api/artists/profile/me`, { headers }).catch(() => null);
      const [prodRes, ordRes, relRes] = await Promise.all([
        axios.get(`${API_URL}/api/store/products?artist_id=${user.id}&limit=100`, { headers }).catch(() => null),
        axios.get(`${API_URL}/api/store/artist/orders/my`, { headers }).catch(() => null),
        axios.get(`${API_URL}/api/artists/releases/mine`, { headers }).catch(() => null)
      ]);
      setProducts(prodRes?.data?.products || []);
      setOrders(ordRes?.data?.orders || []);
      setReleases(relRes?.data?.releases || []);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => { load(); }, [load]);

  const delRelease = async (id) => {
    const token = localStorage.getItem('token');
    await axios.delete(`${API_URL}/api/artists/releases/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const del = async (id) => {
    const token = localStorage.getItem('token');
    await axios.delete(`${API_URL}/api/store/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setConfirmDelete(null);
    load();
  };

  return (
    <div className="min-h-screen bg-[#050507] pb-12" data-testid="store-manager-page">
      <AmbientGlow />
      <div className="sticky top-0 z-20 bg-[#0B0A10]/90 backdrop-blur-xl border-b border-white/10 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center flex-shrink-0" data-testid="store-manager-back">
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-white tracking-tight leading-none" style={{ fontFamily: 'Unbounded' }}>Store Manager</h1>
            <p className="text-[#71717A] text-xs mt-1">Sell merch, music & tickets to your fans</p>
          </div>
          <button
            onClick={() => { setEditing(null); setFormOpen(true); }}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white text-sm font-semibold flex-shrink-0"
            data-testid="upload-merch-btn"
          >
            <Plus className="w-4 h-4" /> Upload Merch
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto flex gap-2 px-4 py-4">
        {['products', 'releases', 'orders'].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors ${tab === t ? 'bg-white text-[#050505]' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'}`}
            data-testid={`store-tab-${t}`}>
            {t === 'products' ? `Products (${products.length})` : t === 'releases' ? `Releases (${releases.length})` : `Orders (${orders.length})`}
          </button>
        ))}
      </div>

      <div className="max-w-3xl mx-auto px-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" /></div>
        ) : tab === 'releases' ? (
          <>
            <button onClick={() => { setEditingRelease(null); setReleaseFormOpen(true); }}
              className="w-full py-3.5 rounded-2xl border border-dashed border-white/20 text-white hover:bg-white/5 hover:border-[#00F0FF]/40 transition-colors flex items-center justify-center gap-2 font-medium" data-testid="add-release-btn">
              <Plus className="w-5 h-5 text-[#00F0FF]" /> Announce a new release
            </button>
            {releases.length === 0 ? (
              <div className="text-center py-16 text-[#71717A]"><Package className="w-14 h-14 mx-auto mb-3 opacity-40" /><p className="text-sm">No releases yet.</p><p className="text-xs mt-1">Announce an upcoming drop — it shows on the Live page.</p></div>
            ) : releases.map((r) => (
              <div key={r.id} className="bg-[#12101A] rounded-2xl border border-white/5 p-3.5 flex items-center gap-4" data-testid={`release-row-${r.id}`}>
                <div className="w-16 h-16 rounded-xl bg-white/10 overflow-hidden flex-shrink-0">
                  {r.cover_image ? <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-[#71717A]" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] uppercase tracking-wider text-[#00F0FF] bg-[#00F0FF]/10 px-1.5 py-0.5 rounded">{r.release_type}</span>
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${r.status === 'released' ? 'text-green-400 bg-green-400/10' : 'text-[#39FF14] bg-[#39FF14]/10'}`}>{r.status}</span>
                  </div>
                  <p className="text-white font-semibold truncate mt-1">{r.title}</p>
                  <p className="text-[#71717A] text-xs mt-0.5">Drops {r.release_date}{r.preorder_enabled ? ` · Pre-order $${Number(r.preorder_price).toFixed(2)} · ${r.preorder_count || 0} sold` : ''}</p>
                </div>
                <button onClick={() => { setEditingRelease(r); setReleaseFormOpen(true); }} className="p-2 rounded-lg bg-white/5 hover:bg-white/10" data-testid={`release-edit-${r.id}`}>
                  <Edit2 className="w-4 h-4 text-white" />
                </button>
                <button onClick={() => delRelease(r.id)} className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20" data-testid={`release-delete-${r.id}`}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            ))}
          </>
        ) : tab === 'products' ? (
          <>
            <button onClick={() => { setEditing(null); setFormOpen(true); }}
              className="w-full py-3.5 rounded-2xl border border-dashed border-white/20 text-white hover:bg-white/5 hover:border-[#00F0FF]/40 transition-colors flex items-center justify-center gap-2 font-medium" data-testid="add-product-btn">
              <ImagePlus className="w-5 h-5 text-[#00F0FF]" /> Add a new product
            </button>
            {products.length === 0 ? (
              <div className="text-center py-16 text-[#71717A]"><ShoppingBag className="w-14 h-14 mx-auto mb-3 opacity-40" /><p className="text-sm">No products yet.</p><p className="text-xs mt-1">Tap “Upload Merch” to add your first item.</p></div>
            ) : products.map((p) => (
              <div key={p.id} className="bg-[#12101A] rounded-2xl border border-white/5 hover:border-white/10 transition-colors p-3.5 flex items-center gap-4" data-testid={`product-row-${p.id}`}>
                <div className="w-16 h-16 rounded-xl bg-white/10 overflow-hidden flex-shrink-0">
                  {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-[#71717A]" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] uppercase tracking-wider text-[#00F0FF] bg-[#00F0FF]/10 px-1.5 py-0.5 rounded">{p.category}</span>
                  </div>
                  <p className="text-white font-semibold truncate mt-1">{p.name || 'Untitled'}</p>
                  <p className="text-[#71717A] text-xs mt-0.5">${Number(p.price || 0).toFixed(2)} · {p.stock ?? 0} in stock · {p.sold_count || 0} sold · {p.vote_count || 0} votes</p>
                </div>
                <button onClick={() => { setEditing(p); setFormOpen(true); }} className="p-2 rounded-lg bg-white/5 hover:bg-white/10" data-testid={`product-edit-${p.id}`}>
                  <Edit2 className="w-4 h-4 text-white" />
                </button>
                <button onClick={() => setConfirmDelete(p)} className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20" data-testid={`product-delete-${p.id}`}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            ))}
          </>
        ) : (
          orders.length === 0 ? (
            <div className="text-center py-12 text-[#71717A]"><Package className="w-12 h-12 mx-auto mb-3 opacity-40" /><p className="text-sm">No orders yet.</p></div>
          ) : orders.map((o) => <OrderCard key={o.id} order={o} onFulfilled={load} />)
        )}
      </div>

      <AnimatePresence>
        {formOpen && <ProductForm initial={editing} onClose={() => setFormOpen(false)} onSaved={load} />}
      </AnimatePresence>

      <AnimatePresence>
        {releaseFormOpen && <ReleaseForm initial={editingRelease} onClose={() => setReleaseFormOpen(false)} onSaved={load} />}
      </AnimatePresence>

      <AnimatePresence>
        {confirmDelete && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-md z-50" onClick={() => setConfirmDelete(null)} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                className="w-full max-w-sm bg-[#12101A] rounded-2xl border border-white/10 p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm">
                <Trash2 className="w-10 h-10 mx-auto text-red-400 mb-3" />
                <p className="text-white font-semibold">Delete "{confirmDelete.name}"?</p>
                <p className="text-[#71717A] text-sm mt-1 mb-4">This can't be undone.</p>
                <div className="flex gap-2">
                  <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 rounded-xl bg-white/10 text-white">Cancel</button>
                  <button onClick={() => del(confirmDelete.id)} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-semibold" data-testid="delete-confirm-btn">Delete</button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StoreManagerPage;
